import type { AppInfo, Readiness } from '@jt/contracts';
import type { Capability } from '@jt/domain';
import type * as T from './types';
import type { SessionUser } from './session';

/*
 * `__Host-gsn_csrf` in production, `gsn_csrf` in development and on a browser
 * still holding a cookie from before ADR-038. Read in that order.
 */
const CSRF_COOKIES = ['__Host-gsn_csrf', 'gsn_csrf'];

/**
 * Raised on `window` when the server refuses this browser's session — ended
 * elsewhere, idle too long, or ended to protect the account — which it says
 * with the `X-Session-Ended` header. The session provider listens and signs the
 * whole browser out, every tab, rather than leaving the console on screen
 * failing one request at a time. A 401 without that header is not this.
 */
export const SESSION_ENDED_EVENT = 'gsn:session-ended';
export type SessionEndedDetail = { reason: string; message: string };

function readCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}

export class ApiError extends Error {
  constructor(
    /** HTTP status, or **0** when the request never reached the server at all. */
    readonly status: number,
    message: string,
    /**
     * The server's correlation id for this failure, when it sent one.
     *
     * Worth showing on screen: "something went wrong, reference 4f21" is a
     * support conversation that can start, and "something went wrong" is not.
     */
    readonly reference?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** True when nothing was sent, so retrying cannot duplicate anything. */
  get neverArrived(): boolean {
    return this.status === 0;
  }
}

/**
 * What to say when the server said nothing useful.
 *
 * Reached when the body is not JSON — nginx's own 502 page, a proxy interposing,
 * a gateway timing out. Before this, the screen showed the status line, so
 * somebody whose connection had wobbled read "502 Bad Gateway" and had no idea
 * whether their work had saved.
 *
 * The application's own 4xx messages are always better than these and always
 * win; nothing here overrides a sentence the server actually sent.
 */
function fallbackSentence(status: number): string {
  if (status === 401) return 'Your session has ended. Sign in again to carry on.';
  if (status === 403) return 'Your account is not allowed to do that.';
  if (status === 404) return 'That is not here. The link may be out of date.';
  if (status === 413) return 'That file is too large. Try one photograph at a time.';
  if (status === 429) return 'Too many attempts in a short time. Wait a few minutes.';
  if (status === 502 || status === 504) {
    return 'The system could not be reached. Try again in a moment — this is usually the connection.';
  }
  if (status >= 500) {
    return 'Something went wrong at our end. Your work was not saved, so try again.';
  }
  return 'That could not be completed. Check what you entered and try again.';
}

/**
 * In dev, Vite proxies /api and /health to the API. In Docker, nginx does.
 * Either way the browser only ever talks to its own origin.
 */
async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const method = init.method ?? 'GET';
  const headers = new Headers(init.headers);
  headers.set('Accept', 'application/json');
  if (init.body) headers.set('Content-Type', 'application/json');

  // Double-submit CSRF: echo the readable cookie back in a header.
  if (method !== 'GET' && method !== 'HEAD') {
    const csrf = CSRF_COOKIES.map(readCookie).find((value) => value !== null) ?? null;
    if (csrf) headers.set('x-csrf-token', csrf);
  }

  /*
   * A dropped connection is not an exception to be shown as one.
   *
   * `fetch` rejects with `TypeError: Failed to fetch` when there is no network,
   * when DNS fails, when the server is unreachable, and when a proxy kills the
   * request. That string reached the screen verbatim, and it is meaningless to
   * everybody who uses this — while being, in Kisii on a bad afternoon, the
   * single most likely failure of all.
   *
   * Status 0 marks it as "never arrived", which the caller can tell apart from a
   * refusal: nothing was attempted, so retrying is always safe.
   */
  let response: Response;
  try {
    response = await fetch(path, { ...init, method, headers, credentials: 'include' });
  } catch {
    throw new ApiError(
      0,
      navigator.onLine === false
        ? 'This device is offline, so nothing was sent. It will work again when the connection comes back.'
        : 'The system could not be reached, so nothing was sent. Check the connection and try again.',
    );
  }

  // /health/ready answers 503 when degraded — a valid body, not a failure.
  if (!response.ok && response.status !== 503) {
    let message = fallbackSentence(response.status);
    let reference: string | undefined;
    try {
      const body = (await response.json()) as {
        message?: string | string[];
        reference?: string;
      };
      if (body.message) {
        // Joined, not first-only: told about one bad field at a time, somebody
        // fixes it, submits, and is then told about the next.
        message = Array.isArray(body.message) ? body.message.join(' ') : body.message;
      }
      reference = body.reference;
    } catch {
      /*
       * A non-JSON error body means the response did not come from the
       * application at all — nginx returning its own 502 page, or a proxy
       * interposing. The status line was shown here before, which told nobody
       * anything; `fallbackSentence` already holds a better one.
       */
    }
    /*
     * Only a refusal the guard *marked* as the end of a session. Other 401s
     * are ordinary answers — a wrong current password, "sign in again" from a
     * service — and wiping the browser over one of those would throw away
     * somebody's work for a typo.
     */
    const ended = response.status === 401 ? response.headers.get('X-Session-Ended') : null;
    if (ended && typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent<SessionEndedDetail>(SESSION_ENDED_EVENT, {
          detail: { reason: ended, message },
        }),
      );
    }
    throw new ApiError(response.status, message, reference);
  }

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

type MeResponse = { user: SessionUser; capabilities: Capability[] };

export const api = {
  readiness: () => request<Readiness>('/health/ready'),
  info: () => request<AppInfo>('/api/v1/info'),

  me: () => request<MeResponse>('/api/v1/auth/me'),
  login: (
    email: string,
    password: string,
    /**
     * What the sign-in screen may add: staying signed in on this device.
     * Optional, so the handset app and the verification scripts are unchanged.
     */
    extra: { remember?: boolean } = {},
  ) =>
    request<MeResponse & { expiresAt: string }>('/api/v1/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password, ...extra }),
    }),
  /**
   * `keepOffline` when a technician's unsent offline actions are waiting: the
   * server then leaves browser storage alone (ADR-038).
   */
  logout: (options: { keepOffline?: boolean } = {}) =>
    request<void>(`/api/v1/auth/logout${options.keepOffline ? '?keep=offline' : ''}`, {
      method: 'POST',
    }),
  mySessions: () => request<{ sessions: T.DeviceSession[] }>('/api/v1/auth/sessions'),
  endMySession: (id: string) =>
    request<void>(`/api/v1/auth/sessions/${id}`, { method: 'DELETE' }),
  endOtherSessions: () =>
    request<{ ended: number }>('/api/v1/auth/sessions/revoke-others', { method: 'POST' }),
  userSessions: (id: string) =>
    request<{ sessions: T.DeviceSession[] }>(`/api/v1/users/${id}/sessions`),
  endUserSessions: (id: string) =>
    request<{ ended: number }>(`/api/v1/users/${id}/sessions/revoke`, { method: 'POST' }),
  /**
   * Always resolves for a well-formed address, whether or not it exists. The
   * screen must not report anything the API deliberately withheld.
   */
  /** Months of trading, for a lender. Needs report:view_cost. */
  businessReport: (months = 12) =>
    request<{
      generatedAt: string;
      period: { months: number; since: string };
      revenueTracked: boolean;
      basis: string;
      customers: {
        total: number; active: number; servedInPeriod: number;
        business: number; home: number;
        newPerMonth: Array<{ month: string; count: number }>;
      };
      delivery: {
        jobsOpened: number; jobsClosed: number;
        completionRate: number | null; slaCompliance: number | null; breached: number;
        medianResolutionHours: number | null; satisfaction: number | null; ratingCount: number;
      };
      costs: {
        totalKes: number; perClosedJobKes: number | null;
        byCategory: Array<{ category: string; amountKes: number }>;
      };
      capacity: {
        technicians: number; jobsPerTechnician: number | null;
        areasCovered: number; networkElements: number;
      };
      monthly: Array<{
        month: string; opened: number; closed: number; breached: number; costKes: number;
      }>;
    }>(`/api/v1/reports/business?months=${months}`),

  /** Only reachable with material:record — technician or admin. */
  materialCatalogue: () =>
    request<{
      catalogue: Array<{
        code: string; name: string; aka: string | null; category: string;
        unit: 'each' | 'metre'; requiresSerial: boolean; maxPerVisit: number | null;
      }>;
      categoryLabels: Record<string, string>;
    }>('/api/v1/tickets/materials/catalogue'),
  setTicketMaterials: (
    id: string,
    materials: Array<{ code: string; quantity: number; serial?: string; note?: string }>,
    materialsOther: string,
  ) =>
    request<{ materials: unknown[]; materialsOther: string | null }>(
      `/api/v1/tickets/${id}/materials`,
      { method: 'PUT', body: JSON.stringify({ materials, materialsOther }) },
    ),

  /** Read-only ticket behind a share link. No session needed. */
  sharedTicket: (token: string) =>
    request<{
      ticket: {
        reference: string; title: string; description: string | null; status: string;
        priority: string; outcome: string | null; jobType: string | null; area: string | null;
        technician: string | null; customer: string | null; element: string | null;
        site: string | null; createdAt: string; scheduledStart: string | null;
        closedAt: string | null;
      };
      materials: Array<{ code: string; quantity: number; serial: string | null; note: string | null }>;
      materialsOther: string | null;
      /** When the technician marked themselves on site, if they have. */
      onSiteAt: string | null;
      timeline: Array<{ status: string | null; at: string; by: string | null }>;
      photoCount: number;
      viewer: { signedIn: boolean; name?: string; role?: string };
      reportPath: string;
    }>(`/api/v1/ticket/${token}`),
  shareTicket: (id: string) =>
    request<{ url: string; expiresAt: string }>(`/api/v1/tickets/${id}/share`, { method: 'POST' }),
  forgotPassword: (email: string) =>
    request<{ message: string }>('/api/v1/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),
  checkResetLink: (token: string) =>
    request<{ valid: boolean; email?: string }>(`/api/v1/auth/reset-password/${token}`),
  resetPassword: (token: string, newPassword: string) =>
    request<void>('/api/v1/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, newPassword }),
    }),
  updateMe: (name: string) =>
    request<{ id: string; name: string; email: string; role: string }>('/api/v1/auth/me', {
      method: 'PATCH',
      body: JSON.stringify({ name }),
    }),
  changePassword: (currentPassword: string, newPassword: string) =>
    request<void>('/api/v1/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    }),

  tickets: (
    params: {
      status?: string;
      areaId?: string;
      q?: string;
      archived?: string;
      /**
       * Only what is assigned to the person asking.
       *
       * Not the same as the role scoping the server already applies. That is
       * about what somebody is allowed to see; this is somebody asking about
       * their own work — and an administrator is allowed to see everything.
       */
      mine?: string;
      /**
       * Only tickets nobody is going to do.
       *
       * The counterpart to the "N unassigned" figure that has been on the
       * tickets page since it was written. The number said there was a
       * problem; nothing said which tickets, which is most of the way to not
       * saying anything.
       */
      unassigned?: string;
      page?: number;
      perPage?: number;
    } = {},
  ) => {
    const search = new URLSearchParams(
      Object.entries(params)
        .filter(([, value]) => value !== undefined && value !== '')
        .map(([key, value]) => [key, String(value)]),
    );
    return request<{ tickets: T.TicketSummary[] } & T.PageInfo>(`/api/v1/tickets?${search}`);
  },
  ticketStats: () =>
    request<{
      byStatus: Record<string, number>;
      breached: number;
      unassigned: number;
      incidents: number;
    }>('/api/v1/tickets/stats'),
  ticket: (id: string) => request<T.TicketDetail>(`/api/v1/tickets/${id}`),
  createTicket: (body: Record<string, unknown>) =>
    request<{ id: string; reference: string }>('/api/v1/tickets', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  assign: (id: string, technicianId: string, channel?: string) =>
    request<T.AssignResult>(`/api/v1/tickets/${id}/assign`, {
      method: 'POST',
      body: JSON.stringify({ technicianId, ...(channel ? { channel } : {}) }),
    }),
  addWatcher: (id: string, body: Record<string, unknown>) =>
    request<{ id: string }>(`/api/v1/tickets/${id}/watchers`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  incidentSuggestions: (id: string) =>
    request<{
      suggestions: Array<{
        incidentId: string;
        reference: string;
        title: string;
        confidence: number;
        reason: string;
      }>;
    }>(`/api/v1/tickets/${id}/incident-suggestions`),
  setIncident: (id: string, isIncident: boolean) =>
    request<{ isIncident: boolean }>(`/api/v1/tickets/${id}/incident`, {
      method: 'POST',
      body: JSON.stringify({ isIncident }),
    }),
  linkIncident: (id: string, incidentId: string) =>
    request<{ message: string; dispatchSuppressed: boolean }>(
      `/api/v1/tickets/${id}/link-incident`,
      { method: 'POST', body: JSON.stringify({ incidentId }) },
    ),
  unlinkIncident: (id: string) =>
    request<{ message: string }>(`/api/v1/tickets/${id}/unlink-incident`, {
      method: 'POST',
      body: '{}',
    }),
  resolveIncident: (id: string, body: { outcome: string; rootCauseCode?: string; note?: string }) =>
    request<{ resolved: string[]; skipped: { reference: string; reason: string }[]; message: string }>(
      `/api/v1/tickets/${id}/resolve-incident`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  ticketActions: (id: string) =>
    request<{ actions: string[] }>(`/api/v1/tickets/${id}/actions`),
  transition: (id: string, event: string, body: Record<string, unknown>) =>
    request<{ from: string; to: string }>(`/api/v1/tickets/${id}/transitions/${event}`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  deadLetters: () =>
    request<{
      messages: Array<{
        id: string; channel: string; toAddress: string; kind: string; attempts: number;
        errorCode: string | null; errorDetail: string | null; deadLetteredAt: string | null;
        job: { reference: string; title: string } | null;
      }>;
    }>('/api/v1/outbox/dead-letters'),
  replayDeadLetter: (id: string) =>
    request<{ replayed: boolean }>('/api/v1/outbox/dead-letters/' + id + '/replay', { method: 'POST' }),
  /* For the ones the network refused outright, which replaying cannot help. */
  resolveDeadLetter: (id: string) =>
    request<{ resolved: boolean }>('/api/v1/outbox/dead-letters/' + id + '/resolve', { method: 'POST' }),

  users: () =>
    request<{
      users: Array<{
        id: string; email: string; name: string; role: string;
        isActive: boolean; lastLoginAt: string | null;
        // Still holding a password somebody else chose, and when they last
        // picked their own. A handover nobody completed reads as a normal
        // account without these.
        mustChangePassword: boolean; passwordChangedAt: string | null;
        /** The roster record this login belongs to. Null means unlinked. */
        technicianId: string | null;
        /**
         * Jobs they do beyond the one in `role`, and the groups they are in.
         *
         * On the list rather than one click into each record, because with
         * additional roles in play the role column no longer answers "who can
         * do X" — which is the question this screen is usually open for.
         */
        extraRoles: string[];
        groups: string[];
      }>;
    }>('/api/v1/users'),
  createUser: (body: Record<string, unknown>) =>
    request<{ id: string; email: string; name: string; role: string; invite: T.Invite }>(
      '/api/v1/users',
      { method: 'POST', body: JSON.stringify(body) },
    ),
  updateUser: (id: string, body: Record<string, unknown>) =>
    request<{ id: string }>(`/api/v1/users/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  resetUserPassword: (id: string, password: string) =>
    request<{ reset: boolean; invite: T.Invite }>(`/api/v1/users/${id}/reset-password`, {
      method: 'POST',
      body: JSON.stringify({ password }),
    }),

  /* ── KPLC prepaid power ────────────────────────────────────────────── */

  powerMeters: () => request<T.PowerMeter[]>('/api/v1/power/meters'),
  powerPlan: (onlyDue = false) =>
    request<T.PowerPlan>(`/api/v1/power/plan${onlyDue ? '?due=true' : ''}`),
  powerOutstanding: () => request<T.PowerOutstanding[]>('/api/v1/power/in-flight'),
  /** The header search. Encoded, because a reference can contain a slash. */
  search: (q: string) =>
    request<T.SearchHit[]>(`/api/v1/search?q=${encodeURIComponent(q)}`),
  powerReadiness: () => request<T.PowerReadiness>('/api/v1/power/readiness'),
  /**
   * Whether a trigger address checks what it is asked.
   *
   * Never fires the real macro — it asks the same device for one that cannot
   * exist, because some addresses answer OK to anything and a success from
   * those proves nothing.
   */
  /** Switch to a saved handset: paying number and both triggers, together. */
  /** Delete a meter. Refused once anything has been bought for it. */
  removePowerMeter: (id: string) =>
    request<{ ok: true }>(`/api/v1/power/meters/${id}`, { method: 'DELETE' }),
  powerUsePhone: (id: string) =>
    request<{ ok: true; label: string }>(`/api/v1/power/phones/${id}/use`, { method: 'POST' }),
  powerTestTrigger: (which: 'dial' | 'send') =>
    request<T.PowerTriggerTest>(`/api/v1/power/triggers/${which}/test`, { method: 'POST' }),
  /**
   * The paying-phone app build available to install, if one has been uploaded.
   *
   * `available: false` is an ordinary answer, not an error — the endpoint
   * exists before the first build does.
   */
  payphoneLatest: () =>
    request<{
      available: boolean;
      build?: {
        version: string;
        sha256: string;
        size: number;
        uploadedAt: string;
        notes: string | null;
      };
    }>('/api/v1/payphone/latest'),
  /** The URL a phone forwards KPLC messages to. Rotating invalidates the old one. */
  powerSmsLink: (rotate = false) =>
    request<{ url: string; rotated: boolean }>('/api/v1/power/sms-link', {
      method: 'POST',
      body: JSON.stringify({ rotate }),
    }),
  addPowerMeter: (body: Record<string, unknown>) =>
    request<{ id: string }>('/api/v1/power/meters', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /**
   * Correct a meter, or stop topping it up.
   *
   * There is no delete and there should not be: every token purchase ever made
   * against a meter is a payment that really happened, and the row is what
   * those purchases name. Switching it off takes it out of the run and leaves
   * all of that where it is.
   */
  updatePowerMeter: (id: string, body: Record<string, unknown>) =>
    request<T.PowerMeter>(`/api/v1/power/meters/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  /**
   * One meter or forty — the same call.
   *
   * The endpoint has always taken a list, and paying a single meter is a list
   * of one. Keeping it that way means the record a one-off payment leaves is
   * the same shape as the record a month-end run leaves, which is what makes
   * them add up at the end of the month.
   */
  payPower: (meterIds: string[], payerPhone: string) =>
    request<T.PowerPayResult>('/api/v1/power/pay', {
      method: 'POST',
      body: JSON.stringify({ meterIds, payerPhone }),
    }),
  recordPowerToken: (purchaseId: string, token: string, units?: number) =>
    request<{ delivered: boolean; token: string; sentTo: string }>(
      `/api/v1/power/purchases/${purchaseId}/token`,
      { method: 'POST', body: JSON.stringify(units ? { token, units } : { token }) },
    ),
  failPowerPurchase: (purchaseId: string, reason: string) =>
    request<{ failed: boolean }>(`/api/v1/power/purchases/${purchaseId}/failed`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),

  /**
   * Correct a payment that has not been made yet — the amount, or the phone
   * that is going to pay. Refused once Safaricom has confirmed it.
   */
  editPowerPurchase: (purchaseId: string, body: Record<string, unknown>) =>
    request<{ ok: boolean; message: string }>(`/api/v1/power/purchases/${purchaseId}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  /** Every link in the payment chain, and the last failure at each. */
  powerDiagnostics: () => request<T.PowerDiagnostics>('/api/v1/power/diagnostics'),

  /**
   * Try a payment again. Refused on anything already paid, so it can only
   * ever repeat an attempt that reached nobody.
   */
  retryPowerPurchase: (purchaseId: string) =>
    request<{ ok: boolean; message: string }>(`/api/v1/power/purchases/${purchaseId}/retry`, {
      method: 'POST',
    }),

  /** Payments and forwarded messages, together, newest first. */
  powerActivity: (params: { meterId?: string; trash?: boolean } = {}) => {
    const search = new URLSearchParams();
    if (params.meterId) search.set('meterId', params.meterId);
    if (params.trash) search.set('trash', 'true');
    const qs = search.toString();
    return request<T.PowerActivity[]>(`/api/v1/power/activity${qs ? `?${qs}` : ''}`);
  },

  /** Remove payments that never took money. Anything that did is kept. */
  discardAbandonedPayments: (ids?: string[]) =>
    request<{ removed: number; message: string }>('/api/v1/power/purchases/discard', {
      method: 'POST',
      body: JSON.stringify(ids ? { ids } : {}),
    }),

  /** A landlord, and every token that has reached them or is waiting to. */
  landlordTokens: (meterId: string) =>
    request<T.LandlordTokens>(`/api/v1/power/meters/${meterId}/tokens`),

  /** Put one of those tokens on the landlord's phone. It can only go once. */
  sendLandlordToken: (meterId: string, source: string, tokenId: string, language?: string) =>
    request<{ ok: boolean; message: string }>(`/api/v1/power/meters/${meterId}/tokens/send`, {
      method: 'POST',
      body: JSON.stringify({ source, tokenId, language }),
    }),

  /* ── Messages forwarded from the paying phone ──────────────────────── */

  forwardedMessages: (params: { state?: string; meterId?: string; q?: string } = {}) => {
    const search = new URLSearchParams();
    if (params.state && params.state !== 'all') search.set('state', params.state);
    if (params.meterId) search.set('meterId', params.meterId);
    if (params.q) search.set('q', params.q);
    const qs = search.toString();
    return request<T.ForwardedMessage[]>(`/api/v1/power/messages${qs ? `?${qs}` : ''}`);
  },

  /** Say which landlord a message belongs to. Filing it does not send it. */
  assignForwardedMessage: (id: string, meterId: string) =>
    request<{ ok: boolean; message: string }>(`/api/v1/power/messages/${id}/assign`, {
      method: 'POST',
      body: JSON.stringify({ meterId }),
    }),

  /** Correct what a parser read out of somebody else's SMS. */
  updateForwardedMessage: (id: string, body: Record<string, unknown>) =>
    request<{ ok: boolean; message: string }>(`/api/v1/power/messages/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  /**
   * The button. It leaves the building and cannot be taken back, which is why
   * the API refuses a second press rather than trusting the screen to.
   */
  sendForwardedMessage: (id: string, language?: string) =>
    request<{ ok: boolean; message: string; preview: string }>(
      `/api/v1/power/messages/${id}/send`,
      { method: 'POST', body: JSON.stringify({ language }) },
    ),

  /** Put away, not destroyed — it is the only evidence the money left. */
  removeForwardedMessage: (id: string) =>
    request<{ ok: boolean; message: string }>(`/api/v1/power/messages/${id}`, {
      method: 'DELETE',
    }),

  /**
   * Gone, not put away.
   *
   * The trash above keeps everything and says so, which is right when a
   * message is evidence the money left — and wrong when the reason for
   * removing it is that it should never have been collected. There is no
   * restore for this one.
   */
  purgeForwardedMessage: (id: string) =>
    request<{ ok: boolean; message: string }>(`/api/v1/power/messages/${id}/forever`, {
      method: 'DELETE',
    }),

  restoreForwardedMessage: (id: string) =>
    request<{ ok: boolean; message: string }>(`/api/v1/power/messages/${id}/restore`, {
      method: 'POST',
    }),

  /* ── Tech news ─────────────────────────────────────────────────────── */

  news: (params: { source?: string; q?: string } = {}) => {
    const search = new URLSearchParams();
    if (params.source) search.set('source', params.source);
    if (params.q) search.set('q', params.q);
    const query = search.toString();
    return request<T.NewsList>(`/api/v1/news${query ? `?${query}` : ''}`);
  },
  newsStory: (id: string) => request<T.NewsStoryDetail>(`/api/v1/news/${id}`),
  addNewsComment: (id: string, body: string) =>
    request<T.NewsComment>(`/api/v1/news/${id}/comments`, {
      method: 'POST',
      body: JSON.stringify({ body }),
    }),
  removeNewsComment: (commentId: string) =>
    request<{ removed: boolean }>(`/api/v1/news/comments/${commentId}`, { method: 'DELETE' }),
  hideNewsStory: (id: string) =>
    request<{ hidden: boolean }>(`/api/v1/news/${id}/hide`, { method: 'POST' }),

  /* ── Developer options ─────────────────────────────────────────────── */

  developerOverview: () => request<T.DeveloperOverview>('/api/v1/developer/overview'),
  loadDemoData: () =>
    request<{ loaded: number; batch: string }>('/api/v1/developer/demo-data/load', {
      method: 'POST',
    }),
  purgeDemoData: (batch?: string) =>
    request<{
      removed: number;
      failed: number;
      refusals: Array<{ model: string; why: string }>;
      remaining: number;
    }>('/api/v1/developer/demo-data/purge', {
      method: 'POST',
      body: JSON.stringify({ batch }),
    }),

  /* ── More than one job ─────────────────────────────────────────────── */

  userAccess: (id: string) =>
    request<T.UserAccess>(`/api/v1/access/users/${id}`),
  grantRole: (id: string, role: string, note?: string) =>
    request<{ granted: boolean; alreadyHeld: boolean; role: string }>(
      `/api/v1/access/users/${id}/roles`,
      { method: 'POST', body: JSON.stringify({ role, note }) },
    ),
  revokeRole: (id: string, role: string) =>
    request<{ revoked: boolean }>(`/api/v1/access/users/${id}/roles/${role}`, {
      method: 'DELETE',
    }),

  userGroups: () => request<T.UserGroupList>('/api/v1/access/groups'),

  /* ── People asking to be let in ────────────────────────────────────── */

  accessRequests: (status?: string) =>
    request<T.AccessRequestList>(
      `/api/v1/access/requests${status ? `?status=${status}` : ''}`,
    ),
  approveRequest: (id: string, role: string) =>
    request<{ approved: boolean; userId: string; role: string; activationUrl: string }>(
      `/api/v1/access/requests/${id}/approve`,
      { method: 'POST', body: JSON.stringify({ role }) },
    ),
  /** Undo a rejection so they can be considered again. */
  reopenRequest: (id: string) =>
    request<{ id: string; status: string }>(`/api/v1/access/requests/${id}/reopen`, {
      method: 'POST',
    }),
  rejectRequest: (id: string, reason?: string) =>
    request<{ rejected: boolean }>(`/api/v1/access/requests/${id}/reject`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),
  resendActivation: (id: string) =>
    request<{ sent: boolean; activationUrl: string }>(
      `/api/v1/access/requests/${id}/resend`,
      { method: 'POST' },
    ),

  /** Public — the sign-in page asks before drawing any provider buttons. */
  signInProviders: () =>
    request<{ providers: Array<{ provider: string; label: string; startPath: string }> }>(
      '/api/v1/auth/providers',
    ),
  activateAccount: (token: string) =>
    request<{ activated: boolean; alreadyActivated: boolean; name: string }>(
      '/api/v1/auth/activate',
      { method: 'POST', body: JSON.stringify({ token }) },
    ),
  createGroup: (body: { name: string; description?: string; roles: string[] }) =>
    request<{ id: string }>('/api/v1/access/groups', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  updateGroup: (
    id: string,
    body: { name?: string; description?: string; roles?: string[]; isActive?: boolean },
  ) =>
    request<{ id: string }>(`/api/v1/access/groups/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  deleteGroup: (id: string) =>
    request<{ deleted: boolean }>(`/api/v1/access/groups/${id}`, { method: 'DELETE' }),
  addGroupMember: (groupId: string, userId: string) =>
    request<{ added: boolean; alreadyIn: boolean }>(`/api/v1/access/groups/${groupId}/members`, {
      method: 'POST',
      body: JSON.stringify({ userId }),
    }),
  removeGroupMember: (groupId: string, userId: string) =>
    request<{ removed: boolean }>(`/api/v1/access/groups/${groupId}/members/${userId}`, {
      method: 'DELETE',
    }),

  /** Every technician's field allowances for a month — the payroll run. */
  allowances: (month: string) =>
    request<{ month: string; rows: T.AllowanceSummaryRow[]; totalKes: number }>(
      `/api/v1/technicians/allowances?month=${month}`,
    ),
  /** One technician's statement, with every job that contributed. */
  allowanceStatement: (id: string, month: string) =>
    request<T.AllowanceStatementRow>(
      `/api/v1/technicians/${id}/allowances?month=${month}`,
    ),
  /** Your own, whatever the roster says about anyone else. */
  myAllowances: (month: string) =>
    request<T.AllowanceStatementRow>(`/api/v1/technicians/me/allowances?month=${month}`),
  technicianPerformance: (days = 30) =>
    request<{ days: number; technicians: T.TechnicianPerformance[] }>(
      `/api/v1/technicians/performance?days=${days}`,
    ),
  myPerformance: (days = 30) =>
    request<{ days: number; performance: T.TechnicianPerformance | null }>(
      `/api/v1/technicians/me/performance?days=${days}`,
    ),

  assistantStatus: () =>
    request<{ enabled: boolean; suggestions: string[] }>('/api/v1/assistant'),
  assistantAsk: (question: string) =>
    request<T.AssistantAnswer>('/api/v1/assistant/ask', {
      method: 'POST',
      body: JSON.stringify({ question }),
    }),

  myProfile: () =>
    request<{ profile: T.MyProfile | null; knownSkills: string[] }>('/api/v1/technicians/me'),
  updateMyProfile: (patch: Record<string, unknown>) =>
    request<{ profile: T.MyProfile }>('/api/v1/technicians/me', {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),

  /** Metadata only — the token is stored hashed and cannot be read back. */
  ticketLinkStatus: (id: string) =>
    request<{
      assigned: boolean;
      live: boolean;
      expiresAt: string | null;
      issuedAt?: string | null;
      technician: { id: string; name: string } | null;
    }>(`/api/v1/tickets/${id}/link`),
  issueTicketLink: (id: string) =>
    request<{ url: string; expiresAt: string; technician: { id: string; name: string } }>(
      `/api/v1/tickets/${id}/link`,
      { method: 'POST', body: '{}' },
    ),
  resendTicketLink: (id: string) =>
    request<{ sent: boolean; technician: string }>(`/api/v1/tickets/${id}/link/resend`, {
      method: 'POST',
      body: '{}',
    }),

  ticketAttachments: (id: string) =>
    request<{ attachments: T.Attachment[] }>(`/api/v1/tickets/${id}/attachments`),
  /**
   * The <img src> for one photo. Not a fetch: the browser requests it directly
   * and the session cookie rides along, so the API applies the same check it
   * applies to the ticket itself.
   */
  attachmentUrl: (ticketId: string, attachmentId: string) =>
    `/api/v1/tickets/${ticketId}/attachments/${attachmentId}`,

  updateTicket: (id: string, body: Record<string, unknown>) =>
    request<{ id: string }>(`/api/v1/tickets/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  /** Admin only. The API refuses if the ticket has any real history behind it. */
  deleteTicket: (id: string, reason: string) =>
    request<{ deleted: boolean; reference: string }>('/api/v1/tickets/' + id, {
      method: 'DELETE',
      body: JSON.stringify({ reason }),
    }),
  restoreTicket: (id: string) =>
    request<{ restored: boolean }>(`/api/v1/tickets/${id}/restore`, {
      method: 'POST',
      body: '{}',
    }),
  archiveTicket: (id: string, reason: string) =>
    request<{ archived: boolean }>(`/api/v1/tickets/${id}/archive`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),

  aiIntake: (text: string) =>
    request<{
      available: boolean;
      reason?: string;
      runId?: string;
      suggestion?: {
        title: string; description: string; jobTypeId: string | null; jobTypeCode: string | null;
        suggestedPriority: string; priorityReason: string; areaName: string | null;
        customerId: string | null; missingInfo: string[]; safetyFlags: string[];
        estDurationMin: number | null; confidence: number;
      };
    }>('/api/v1/ai/intake', { method: 'POST', body: JSON.stringify({ text }) }),
  aiFeedback: (runId: string, accepted: boolean, correction?: unknown) =>
    request<{ recorded: boolean }>('/api/v1/ai/feedback', {
      method: 'POST',
      body: JSON.stringify({ runId, accepted, correction }),
    }),

  /** Unpaged — every match. What a picker needs. */
  areas: (q?: string) =>
    request<{ areas: T.AreaOption[] }>(`/api/v1/areas${q ? `?q=${encodeURIComponent(q)}` : ''}`),
  /** Paged — what the areas screen reads. */
  areaPage: (params: { q?: string; page?: number; perPage?: number } = {}) => {
    const query = new URLSearchParams(
      Object.entries({ page: 1, perPage: 25, ...params })
        .filter(([, value]) => value !== undefined && value !== '')
        .map(([key, value]) => [key, String(value)]),
    ).toString();
    return request<{ areas: T.AreaOption[] } & T.PageInfo>(`/api/v1/areas?${query}`);
  },
  zones: () =>
    request<{
      zones: Array<{ id: string; code: string; name: string; travelMin: number; areaCount: number }>;
    }>('/api/v1/zones'),

  addArea: (payload: {
    name: string;
    zoneId: string;
    subCounty?: string;
    travelMinFromTown?: number;
    landmarkNotes?: string;
  }) => request<T.AreaOption>('/api/v1/areas', { method: 'POST', body: JSON.stringify(payload) }),

  editArea: (
    id: string,
    payload: {
      name?: string;
      zoneId?: string;
      subCounty?: string;
      travelMinFromTown?: number;
      landmarkNotes?: string;
      active?: boolean;
    },
  ) =>
    request<T.AreaOption>(`/api/v1/areas/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),

  /**
   * Retire an area, or delete one nothing has used.
   *
   * Which of the two happened is in the response rather than assumed here: an
   * area named on old jobs cannot be deleted without taking their history, so
   * it is deactivated, and the screen says so.
   */
  removeArea: (id: string) =>
    request<{ retired: boolean; deleted: boolean; name: string; used: number; message: string }>(
      `/api/v1/areas/${id}`,
      { method: 'DELETE' },
    ),
  dashboard: (days = 30) => request<T.DashboardSummary>(`/api/v1/dashboard?days=${days}`),

  systemReport: (from: string, to: string) =>
    request<T.SystemReport>(`/api/v1/reports/system?from=${from}&to=${to}`),
  systemCsvUrl: (from: string, to: string) =>
    `/api/v1/reports/system.csv?from=${from}&to=${to}`,
  ticketReport: (id: string) => request<Record<string, unknown>>(`/api/v1/reports/ticket/${id}`),

  attendanceToday: () =>
    request<{ workDate: string; checkedIn: boolean; record: T.AttendanceRecord | null }>(
      '/api/v1/attendance/today',
    ),
  checkIn: (where: { lat?: number; lng?: number }) =>
    request<{ record: T.AttendanceRecord }>('/api/v1/attendance/check-in', {
      method: 'POST',
      body: JSON.stringify(where),
    }),
  checkOut: (where: { lat?: number; lng?: number }) =>
    request<{ record: T.AttendanceRecord }>('/api/v1/attendance/check-out', {
      method: 'POST',
      body: JSON.stringify(where),
    }),
  attendanceRoster: (date?: string) =>
    request<T.AttendanceRoster>(`/api/v1/attendance${date ? `?date=${date}` : ''}`),

  expenses: (params: { status?: string; days?: number } = {}) => {
    const search = new URLSearchParams();
    if (params.status) search.set('status', params.status);
    if (params.days) search.set('days', String(params.days));
    return request<{ expenses: T.Expense[]; totals: Record<string, number> }>(
      `/api/v1/expenses?${search}`,
    );
  },
  createExpense: (body: Record<string, unknown>) =>
    request<{ id: string }>('/api/v1/expenses', { method: 'POST', body: JSON.stringify(body) }),
  decideExpense: (id: string, decision: 'approved' | 'rejected', note?: string) =>
    request<{ id: string }>(`/api/v1/expenses/${id}/decision`, {
      method: 'POST',
      body: JSON.stringify({ decision, note }),
    }),

  leave: (params: { status?: string } = {}) =>
    request<{ requests: T.LeaveRequest[] }>(
      `/api/v1/leave${params.status ? `?status=${params.status}` : ''}`,
    ),
  /** What you have earned, taken and have left. Your own only. */
  myLeaveBalance: () =>
    request<{
      balance: {
        accrued: number;
        taken: number;
        pending: number;
        remaining: number;
        annualEntitlement: number;
        employedSince: string;
        employmentDateAssumed: boolean;
      } | null;
      reason?: string;
    }>('/api/v1/leave/balance'),

  requestLeave: (body: Record<string, unknown>) =>
    request<{ id: string }>('/api/v1/leave', { method: 'POST', body: JSON.stringify(body) }),
  decideLeave: (id: string, decision: 'approved' | 'rejected', note?: string) =>
    request<{ id: string }>(`/api/v1/leave/${id}/decision`, {
      method: 'POST',
      body: JSON.stringify({ decision, note }),
    }),
  leaveImpact: (id: string) =>
    request<{ alsoOff: Array<{ name: string; from: string; to: string }>; openJobs: number }>(
      `/api/v1/leave/${id}/impact`,
    ),

  addNote: (id: string, body: string) =>
    request<{ added: boolean }>(`/api/v1/tickets/${id}/notes`, {
      method: 'POST',
      body: JSON.stringify({ body }),
    }),

  audit: (params: { action?: string; actorId?: string; hours?: number; limit?: number } = {}) => {
    const search = new URLSearchParams();
    if (params.action) search.set('action', params.action);
    if (params.actorId) search.set('actorId', params.actorId);
    if (params.hours) search.set('hours', String(params.hours));
    search.set('limit', String(params.limit ?? 100));
    return request<{ events: T.AuditEvent[] }>(`/api/v1/audit?${search}`);
  },
  auditSignals: (hours = 24) =>
    request<T.AuditSignals>(`/api/v1/audit/signals?hours=${hours}`),
  auditActors: () =>
    request<{ actors: Array<{ id: string; name: string }> }>('/api/v1/audit/actors'),

  settings: () => request<T.SettingsPayload>('/api/v1/settings'),
  /**
   * A merge patch, one level deeper than the type used to allow.
   *
   * `Partial<SettingsPayload>` says every top-level block is optional and then
   * insists each one present is complete — so changing a single power setting
   * meant sending the whole block back, including the redacted `smsToken`,
   * which would have overwritten the real secret with a row of dots. The
   * endpoint has always merged; the type now says so.
   */
  updateSettings: (patch: T.SettingsPatch) =>
    request<T.SettingsPayload>('/api/v1/settings', { method: 'PUT', body: JSON.stringify(patch) }),
  sendTestEmail: (to: string) =>
    request<{ ok: boolean; provider: string; detail: string; hint?: string }>(
      '/api/v1/settings/test/email/send',
      { method: 'POST', body: JSON.stringify({ to }) },
    ),
  diagnostics: () =>
    request<{
      checkedAt: string;
      checks: Array<{
        id: string; label: string; status: 'ok' | 'warn' | 'fail';
        detail: string; fix?: string;
      }>;
    }>('/api/v1/settings/diagnostics'),
  testEmail: () =>
    request<{ ok: boolean; detail: string }>('/api/v1/settings/test/email', { method: 'POST' }),

  jobTypes: () => request<{ jobTypes: T.JobType[] }>('/api/v1/job-types'),

  createTechnician: (body: Record<string, unknown>) =>
    request<{ id: string; name: string }>('/api/v1/technicians', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  updateTechnician: (id: string, body: Record<string, unknown>) =>
    request<{ id: string; name: string }>(`/api/v1/technicians/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  technicians: (params: { page?: number; perPage?: number } = {}) => {
    const query = new URLSearchParams(
      Object.entries(params)
        .filter(([, value]) => value !== undefined)
        .map(([key, value]) => [key, String(value)]),
    ).toString();
    return request<{ technicians: T.TechnicianRow[] } & T.PageInfo>(
      `/api/v1/technicians${query ? `?${query}` : ''}`,
    );
  },
  candidates: (areaId?: string, skills?: string[]) => {
    const search = new URLSearchParams();
    if (areaId) search.set('areaId', areaId);
    if (skills?.length) search.set('skills', skills.join(','));
    return request<{ zoneCode: string | null; areaName: string | null; candidates: T.Candidate[] }>(
      `/api/v1/technicians/candidates?${search}`,
    );
  },
  createNetworkElement: (body: Record<string, unknown>) =>
    request<{ id: string }>('/api/v1/network', { method: 'POST', body: JSON.stringify(body) }),
  updateNetworkElement: (id: string, body: Record<string, unknown>) =>
    request<{ id: string }>('/api/v1/network/' + id, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteNetworkElement: (id: string) =>
    request<{ deleted: boolean; name: string }>('/api/v1/network/' + id, { method: 'DELETE' }),

  network: () =>
    request<{
      elements: Array<{
        id: string;
        parentId: string | null;
        type: string;
        name: string;
        identifier: string | null;
        status: string;
        areaId: string | null;
        area: { id: string; name: string; zone: { code: string; name: string } } | null;
      }>;
      /** Every served area with a count, so a gap in coverage is visible. */
      coverage: Array<{
        id: string;
        name: string;
        zoneCode: string;
        zoneName: string;
        elementCount: number;
      }>;
    }>('/api/v1/network'),
  descendants: (id: string) =>
    request<{
      root: { id: string; name: string; type: string };
      descendants: Array<{ id: string; name: string; type: string; status: string; depth: number }>;
      affectedCustomers: number;
    }>(`/api/v1/network/${id}/descendants`),
  customers: () =>
    request<{
      customers: Array<{
        id: string;
        name: string;
        phoneE164: string;
        sites: Array<{ id: string; label: string; areaId: string; areaName: string }>;
      }>;
    }>('/api/v1/catalogue/customers'),

  /* ── Notifications ─────────────────────────────────────────────────────
   *
   * Held by every role, customers included. The server filters to the
   * caller's own id and their own role's announcements, so "everyone may
   * read their tray" is exactly as wide as it sounds.
   */
  notifications: (limit = 40) =>
    request<{ notifications: T.Notification[]; unread: number }>(
      `/api/v1/notifications?limit=${limit}`,
    ),
  /** Polled by the shell on every screen, which is why it is not the full list. */
  unreadCount: () => request<{ unread: number }>('/api/v1/notifications/unread-count'),
  markNotificationRead: (id: string) =>
    request<{ read: boolean; unread: number }>(`/api/v1/notifications/${id}/read`, {
      method: 'POST',
    }),
  markAllNotificationsRead: () =>
    request<{ marked: number }>('/api/v1/notifications/read-all', { method: 'POST' }),
  releases: () => request<{ releases: T.Release[] }>('/api/v1/notifications/releases'),
  broadcast: (payload: { title: string; body: string; audience: string[]; linkTo?: string }) =>
    request<{ sent: boolean }>('/api/v1/notifications/broadcast', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  /* ── Contracts ─────────────────────────────────────────────────────────── */
  contractReferenceData: () =>
    request<{
      plans: T.Plan[];
      variables: string[];
      statuses: string[];
      templates: T.ContractTemplateSummary[];
    }>('/api/v1/contracts/reference-data'),
  contracts: (
    params: { status?: string; customerId?: string; q?: string; page?: number; perPage?: number } = {},
  ) => {
    const query = new URLSearchParams(
      Object.entries(params)
        .filter(([, value]) => value !== undefined && value !== '')
        .map(([key, value]) => [key, String(value)]),
    ).toString();
    return request<{ contracts: T.ContractSummary[] } & T.PageInfo>(
      `/api/v1/contracts${query ? `?${query}` : ''}`,
    );
  },
  contract: (id: string) => request<T.ContractDetail>(`/api/v1/contracts/${id}`),
  contractPreview: (id: string) =>
    request<{ body: string; missing: string[]; unknown: string[] }>(
      `/api/v1/contracts/${id}/preview`,
    ),
  contractRenewals: (withinDays = 60) =>
    request<{ contracts: T.ContractSummary[] }>(
      `/api/v1/contracts/renewals?withinDays=${withinDays}`,
    ),
  createContract: (payload: Record<string, unknown>) =>
    request<T.ContractDetail>('/api/v1/contracts', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  updateContract: (id: string, payload: Record<string, unknown>) =>
    request<T.ContractDetail>(`/api/v1/contracts/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),
  /**
   * Freezes a finished draft and puts it up for the company to sign.
   *
   * Nothing reaches the customer here. `signContractForCompany` is what
   * releases it, and it is a different capability — a salesperson prepares the
   * offer, somebody who can bind the business signs it.
   */
  sendContract: (id: string) =>
    request<T.ContractDetail>(`/api/v1/contracts/${id}/send`, { method: 'POST' }),
  /** Signs for the business, which issues the customer's link and emails it. */
  signContractForCompany: (
    id: string,
    payload: { typedName: string; signatoryName?: string; signatoryTitle?: string },
  ) =>
    request<T.ContractDetail>(`/api/v1/contracts/${id}/sign/company`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  resendContract: (id: string) =>
    request<{ sent: boolean; to: string }>(`/api/v1/contracts/${id}/resend`, { method: 'POST' }),
  amendContract: (id: string, reason: string) =>
    request<T.ContractDetail>(`/api/v1/contracts/${id}/amend`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),
  voidContract: (id: string, reason: string) =>
    request<T.ContractDetail>(`/api/v1/contracts/${id}/void`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),
  contractTemplates: () =>
    request<{ templates: T.ContractTemplateSummary[] }>('/api/v1/contracts/templates'),
  contractTemplate: (id: string) =>
    request<T.ContractTemplate>(`/api/v1/contracts/templates/${id}`),
  saveContractTemplate: (id: string, payload: Record<string, unknown>) =>
    request<T.ContractTemplate>(`/api/v1/contracts/templates/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),

  /* ── Signing, with no account ──────────────────────────────────────────
   *
   * The only two calls in this file that work without a session, because the
   * person making them has no account and should not need one to agree to buy
   * something.
   */
  contractToSign: (token: string) => request<T.SigningView>(`/api/v1/sign/${token}`),
  signContract: (token: string, payload: { typedName: string; agreed: boolean; email?: string }) =>
    request<{ signed: boolean; reference: string; signedAt: string }>(`/api/v1/sign/${token}`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  /* ── Sales ─────────────────────────────────────────────────────────────── */
  salesReferenceData: () =>
    request<{
      sources: string[];
      statuses: string[];
      lostReasons: string[];
      activityKinds: string[];
      rewardKinds: string[];
      referralStatuses: string[];
      plans: T.Plan[];
    }>('/api/v1/sales/reference-data'),
  leads: (params: { status?: string; mine?: boolean; stale?: boolean; q?: string } = {}) => {
    const query = new URLSearchParams();
    if (params.status) query.set('status', params.status);
    if (params.mine) query.set('mine', 'true');
    if (params.stale) query.set('stale', 'true');
    if (params.q) query.set('q', params.q);
    const suffix = query.toString();
    return request<{ leads: T.Lead[] }>(`/api/v1/sales/leads${suffix ? `?${suffix}` : ''}`);
  },
  lead: (id: string) => request<T.LeadDetail>(`/api/v1/sales/leads/${id}`),
  createLead: (payload: Record<string, unknown>) =>
    request<{ lead: T.LeadDetail; possibleDuplicates: Array<{ id: string; name: string }> }>(
      '/api/v1/sales/leads',
      { method: 'POST', body: JSON.stringify(payload) },
    ),
  updateLead: (id: string, payload: Record<string, unknown>) =>
    request<T.LeadDetail>(`/api/v1/sales/leads/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),
  moveLead: (id: string, payload: { to: string; note?: string; lostReason?: string }) =>
    request<T.LeadDetail>(`/api/v1/sales/leads/${id}/status`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  logLeadActivity: (id: string, payload: { kind: string; body?: string }) =>
    request<T.LeadDetail>(`/api/v1/sales/leads/${id}/activity`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  checkCoverage: (id: string, metres?: number) =>
    request<{ coverage: string; label: string; detail: string }>(
      `/api/v1/sales/leads/${id}/coverage`,
      { method: 'POST', body: JSON.stringify(metres === undefined ? {} : { metres }) },
    ),
  convertLead: (id: string, payload: Record<string, unknown>) =>
    request<{
      customerId: string;
      siteId: string;
      jobId: string;
      jobReference: string;
      referralCode: string | null;
    }>(`/api/v1/sales/leads/${id}/convert`, { method: 'POST', body: JSON.stringify(payload) }),
  referrals: (status?: string) =>
    request<{ referrals: T.Referral[] }>(
      `/api/v1/sales/referrals${status ? `?status=${status}` : ''}`,
    ),
  setReferralReward: (id: string, payload: { kind: string; value: number }) =>
    request<T.Referral>(`/api/v1/sales/referrals/${id}/reward`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),
  approveReferral: (id: string) =>
    request<T.Referral>(`/api/v1/sales/referrals/${id}/approve`, { method: 'POST' }),
  markReferralPaid: (id: string) =>
    request<T.Referral>(`/api/v1/sales/referrals/${id}/paid`, { method: 'POST' }),
  rejectReferral: (id: string, reason: string) =>
    request<T.Referral>(`/api/v1/sales/referrals/${id}/reject`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),
  issueAllReferralCodes: () =>
    request<{ issued: number; skipped: number }>('/api/v1/sales/referral-codes/issue-all', {
      method: 'POST',
    }),
  salesReport: (params: { from?: string; to?: string } = {}) => {
    const query = new URLSearchParams(
      Object.entries(params).filter(([, value]) => Boolean(value)) as [string, string][],
    ).toString();
    return request<T.SalesReport>(`/api/v1/sales/report${query ? `?${query}` : ''}`);
  },

  /* ── Customers ─────────────────────────────────────────────────────────── */
  customerList: (params: { q?: string; page?: number; perPage?: number } = {}) => {
    const query = new URLSearchParams(
      Object.entries(params)
        .filter(([, value]) => value !== undefined && value !== '')
        .map(([key, value]) => [key, String(value)]),
    ).toString();
    return request<{ customers: T.CustomerRow[] } & T.PageInfo>(
      `/api/v1/customers${query ? `?${query}` : ''}`,
    );
  },
  createCustomer: (payload: Record<string, unknown>) =>
    request<T.CustomerRow & { welcomeEmailSent: boolean }>('/api/v1/customers', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  /* ── Billing ───────────────────────────────────────────────────────────── */

  subscriptions: (params: { status?: string; customerId?: string } = {}) => {
    const search = new URLSearchParams();
    if (params.status) search.set('status', params.status);
    if (params.customerId) search.set('customerId', params.customerId);
    const query = search.toString();
    return request<T.SubscriptionRow[]>(`/api/v1/subscriptions${query ? `?${query}` : ''}`);
  },
  createSubscription: (payload: Record<string, unknown>) =>
    request<{ id: string }>('/api/v1/subscriptions', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  updateSubscription: (id: string, payload: Record<string, unknown>) =>
    request<{ id: string; status: string }>(`/api/v1/subscriptions/${id}`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  raiseSubscriptionInvoice: (id: string) =>
    request<{ raised: boolean; number?: string; message?: string }>(
      `/api/v1/subscriptions/${id}/invoice`,
      { method: 'POST' },
    ),

  invoices: (params: { status?: string; customerId?: string; overdue?: boolean } = {}) => {
    const search = new URLSearchParams();
    if (params.status) search.set('status', params.status);
    if (params.customerId) search.set('customerId', params.customerId);
    if (params.overdue) search.set('overdue', 'true');
    const query = search.toString();
    return request<T.InvoiceRow[]>(`/api/v1/invoices${query ? `?${query}` : ''}`);
  },
  invoice: (id: string) => request<T.InvoiceDetail>(`/api/v1/invoices/${id}`),

  createInvoice: (body: {
    customerId: string;
    lines: { description: string; quantity: number; unitPriceCents: number; taxable?: boolean }[];
    dueDate?: string;
    notes?: string;
    issue?: boolean;
  }) =>
    request<{ id: string; number: string; status: string; total: string }>('/api/v1/invoices', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  /** Paid, pending, or not payable — the three an office actually says. */
  setInvoiceStatus: (
    id: string,
    body: { status: 'paid' | 'pending' | 'not_payable'; method?: string; reference?: string; reason?: string },
  ) =>
    request<{ id: string; number: string; status: string }>(`/api/v1/invoices/${id}/status`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  voidInvoice: (id: string, reason: string) =>
    request<{ ok: boolean; message: string }>(`/api/v1/invoices/${id}/void`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),
  runBilling: () =>
    request<{ raised: number; skipped: number; summary: string }>('/api/v1/billing/run', {
      method: 'POST',
    }),
  arrears: () => request<T.Arrears>('/api/v1/billing/arrears'),

  payments: (customerId?: string) =>
    request<T.PaymentRow[]>(
      `/api/v1/payments${customerId ? `?customerId=${customerId}` : ''}`,
    ),
  recordPayment: (payload: Record<string, unknown>) =>
    request<{ id: string; message: string }>('/api/v1/payments', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  collectPayment: (payload: { customerId: string; amountCents?: number; phone?: string }) =>
    request<{ paymentId: string; amount: string; message: string }>('/api/v1/payments/collect', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  paymentStatus: (id: string) =>
    request<{ id: string; status: string; message: string; reference: string | null }>(
      `/api/v1/payments/${id}/status`,
    ),
  mpesaReadiness: () =>
    request<{ configured: boolean; missing: string[]; message: string }>(
      '/api/v1/payments/mpesa/readiness',
    ),

  /* ── Stock ─────────────────────────────────────────────────────────────── */

  stockCatalogue: () => request<T.StockItem[]>('/api/v1/stock/catalogue'),
  stockLocations: () => request<T.StockLocationRow[]>('/api/v1/stock/locations'),

  /* ── The list itself ─────────────────────────────────────────────────── */

  /* ── The lists an administrator curates ──────────────────────────────── */

  jobTypesAll: () => request<{ jobTypes: T.JobTypeRow[] }>('/api/v1/job-types?all=true'),

  addJobType: (payload: {
    code: string;
    name: string;
    category: string;
    defaultPriority?: string;
    estDurationMin?: number;
    allowanceKes?: number;
    allowancePerUnit?: boolean;
    allowanceUnitKey?: string;
    allowanceUnitLabel?: string;
    allowanceUnitDivisor?: number;
    allowanceDailyCap?: number;
  }) => request<T.JobTypeRow>('/api/v1/job-types', { method: 'POST', body: JSON.stringify(payload) }),

  editJobType: (
    id: string,
    payload: {
      name?: string;
      category?: string;
      defaultPriority?: string;
      estDurationMin?: number;
      active?: boolean;
      allowanceKes?: number;
      allowancePerUnit?: boolean;
      allowanceUnitKey?: string;
      allowanceUnitLabel?: string;
      allowanceUnitDivisor?: number;
      allowanceDailyCap?: number;
    },
  ) =>
    request<T.JobTypeRow>(`/api/v1/job-types/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),

  /** Retires it, or deletes it when no ticket ever used it. The answer says which. */
  retireJobType: (id: string) =>
    request<{ retired?: boolean; deleted?: boolean; message: string }>(
      `/api/v1/job-types/${id}`,
      { method: 'DELETE' },
    ),

  zonesAll: () => request<{ zones: T.ZoneRow[] }>('/api/v1/zones'),

  addZone: (payload: { code: string; name: string; defaultTravelMin?: number }) =>
    request<T.ZoneRow>('/api/v1/zones', { method: 'POST', body: JSON.stringify(payload) }),

  editZone: (id: string, payload: { name?: string; defaultTravelMin?: number; active?: boolean }) =>
    request<T.ZoneRow>(`/api/v1/zones/${id}`, { method: 'PATCH', body: JSON.stringify(payload) }),

  retireZone: (id: string) =>
    request<{ retired: boolean; message: string }>(`/api/v1/zones/${id}`, { method: 'DELETE' }),

  editSupplier: (
    id: string,
    payload: {
      name?: string;
      contactName?: string;
      phoneE164?: string;
      email?: string;
      kraPin?: string;
      address?: string;
      paymentTermDays?: number;
      isActive?: boolean;
    },
  ) =>
    request<{ id: string; name: string }>(`/api/v1/suppliers/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),

  retireSupplier: (id: string) =>
    request<{ retired?: boolean; deleted?: boolean; message: string }>(
      `/api/v1/suppliers/${id}`,
      { method: 'DELETE' },
    ),

  stockLocationsAll: () => request<T.StockLocationRow[]>('/api/v1/stock/locations?all=true'),

  editStockLocation: (id: string, payload: { name?: string; isActive?: boolean }) =>
    request<T.StockLocationRow>(`/api/v1/stock/locations/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),

  retireStockLocation: (id: string) =>
    request<{ retired: boolean; message: string }>(`/api/v1/stock/locations/${id}`, {
      method: 'DELETE',
    }),

  inventoryItems: () => request<T.InventoryList>('/api/v1/stock/items'),

  /** Out of the trash, back onto the list as retired. */
  restoreInventoryItem: (id: string) =>
    request<{ restored: boolean; message: string }>(`/api/v1/stock/items/${id}/restore`, {
      method: 'POST',
    }),

  /* ── The categories, which are rows and not a constant ───────────────── */

  inventoryCategories: () =>
    request<{ categories: T.InventoryCategory[] }>('/api/v1/stock/categories'),

  addInventoryCategory: (payload: { label: string; help?: string }) =>
    request<T.InventoryCategory>('/api/v1/stock/categories', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  editInventoryCategory: (
    id: string,
    payload: { label?: string; help?: string | null; sortOrder?: number },
  ) =>
    request<T.InventoryCategory>(`/api/v1/stock/categories/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),

  moveInventoryCategoryItems: (id: string, toId: string) =>
    request<{ moved: number; stuck: number; message: string }>(
      `/api/v1/stock/categories/${id}/move-items`,
      { method: 'POST', body: JSON.stringify({ toId }) },
    ),

  /**
   * Archive, not delete — the endpoint is DELETE because that is the action
   * the person is taking, and the answer says plainly it can be undone.
   */
  archiveInventoryCategory: (id: string, moveTo?: string) =>
    request<{ archived: boolean; message: string }>(`/api/v1/stock/categories/${id}`, {
      method: 'DELETE',
      body: JSON.stringify(moveTo ? { moveTo } : {}),
    }),

  restoreInventoryCategory: (id: string) =>
    request<{ restored: boolean; message: string }>(
      `/api/v1/stock/categories/${id}/restore`,
      { method: 'POST' },
    ),

  addInventoryItem: (payload: {
    code: string;
    name: string;
    aka?: string;
    category: string;
    unit: string;
    requiresSerial?: boolean;
    maxPerVisit?: number;
  }) => request<T.InventoryItem>('/api/v1/stock/items', { method: 'POST', body: JSON.stringify(payload) }),

  editInventoryItem: (
    id: string,
    payload: {
      name?: string;
      aka?: string;
      category?: string;
      unit?: string;
      requiresSerial?: boolean;
      maxPerVisit?: number;
      isActive?: boolean;
    },
  ) =>
    request<T.InventoryItem>(`/api/v1/stock/items/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),

  removeInventoryItem: (id: string) =>
    request<{ retired: boolean; deleted: boolean; used: number; message: string }>(
      `/api/v1/stock/items/${id}`,
      { method: 'DELETE' },
    ),
  ensureStockLocations: () =>
    request<{ created: number; message: string }>('/api/v1/stock/locations/ensure', {
      method: 'POST',
    }),
  stockOverview: () => request<T.StockOverview>('/api/v1/stock/overview'),
  stockBalances: (locationId?: string) =>
    request<T.StockBalanceRow[]>(
      `/api/v1/stock/balances${locationId ? `?locationId=${locationId}` : ''}`,
    ),
  stockMovements: (params: { locationId?: string; code?: string } = {}) => {
    const search = new URLSearchParams();
    if (params.locationId) search.set('locationId', params.locationId);
    if (params.code) search.set('code', params.code);
    const query = search.toString();
    return request<T.StockMovementRow[]>(`/api/v1/stock/movements${query ? `?${query}` : ''}`);
  },
  moveStock: (payload: Record<string, unknown>) =>
    request<{ ok: boolean; message: string }>('/api/v1/stock/movements', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  stockToReorder: () => request<T.ReorderReport>('/api/v1/stock/reorder'),
  setReorderRule: (payload: { code: string; reorderLevel: number; reorderQuantity: number }) =>
    request<{ ok: boolean }>('/api/v1/stock/reorder', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  openStockCount: (locationId: string) =>
    request<{ id: string; locationName: string; lines: number }>('/api/v1/stock/counts', {
      method: 'POST',
      body: JSON.stringify({ locationId }),
    }),
  stockCount: (id: string) => request<T.StockCountDetail>(`/api/v1/stock/counts/${id}`),
  setStockCountLine: (id: string, code: string, counted: number) =>
    request<{ ok: boolean }>(`/api/v1/stock/counts/${id}/lines`, {
      method: 'POST',
      body: JSON.stringify({ code, counted }),
    }),
  applyStockCount: (id: string) =>
    request<{ ok: boolean; summary: string; adjustments: number }>(
      `/api/v1/stock/counts/${id}/apply`,
      { method: 'POST' },
    ),

  /* ── Procurement ───────────────────────────────────────────────────────── */

  suppliers: () => request<T.SupplierRow[]>('/api/v1/suppliers'),

  suppliersAll: () => request<T.SupplierRow[]>('/api/v1/suppliers?all=true'),
  createSupplier: (payload: Record<string, unknown>) =>
    request<{ id: string; name: string }>('/api/v1/suppliers', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  purchaseOrders: (status?: string) =>
    request<T.PurchaseOrderRow[]>(
      `/api/v1/purchase-orders${status ? `?status=${status}` : ''}`,
    ),
  purchaseOrder: (id: string) => request<T.PurchaseOrderDetail>(`/api/v1/purchase-orders/${id}`),
  createPurchaseOrder: (payload: Record<string, unknown>) =>
    request<{ id: string; number: string; total: string }>('/api/v1/purchase-orders', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  sendPurchaseOrder: (id: string) =>
    request<{ ok: boolean; message: string }>(`/api/v1/purchase-orders/${id}/send`, {
      method: 'POST',
    }),
  receivePurchaseOrder: (id: string, payload: Record<string, unknown>) =>
    request<{ ok: boolean; message: string }>(`/api/v1/purchase-orders/${id}/receive`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  payPurchaseOrder: (id: string, amountCents: number) =>
    request<{ ok: boolean; message: string }>(`/api/v1/purchase-orders/${id}/pay`, {
      method: 'POST',
      body: JSON.stringify({ amountCents }),
    }),

  /* ── Your profile photo ────────────────────────────────────────────────── */

  /**
   * `data` is a data URL, the same shape the job form posts a photograph in.
   * The server sniffs the real type from the bytes and refuses anything over
   * 2 MB, so a client that skips its own check is caught rather than trusted.
   */
  setAvatar: (data: string) =>
    request<{ id: string; url: string }>('/api/v1/profile/avatar', {
      method: 'POST',
      body: JSON.stringify({ data }),
    }),
  clearAvatar: () =>
    request<{ cleared: boolean }>('/api/v1/profile/avatar', { method: 'DELETE' }),

  /* ── Network design ────────────────────────────────────────────────────── */

  networkDesign: (optic: 'B+' | 'C+' = 'B+') =>
    request<T.NetworkDesign>(`/api/v1/network/design?optic=${encodeURIComponent(optic)}`),
  networkDownstream: (id: string) =>
    request<{
      elements: { id: string; name: string; type: string }[];
      customerCount: number;
      summary: string;
    }>(`/api/v1/network/design/${id}/downstream`),
  moveNetworkElement: (id: string, x: number, y: number) =>
    request<{ ok: boolean }>(`/api/v1/network/design/${id}/layout`, {
      method: 'POST',
      body: JSON.stringify({ x, y }),
    }),
  setNetworkFigures: (id: string, figures: Record<string, number>) =>
    request<{ ok: boolean }>(`/api/v1/network/design/${id}/figures`, {
      method: 'POST',
      body: JSON.stringify(figures),
    }),

  /* ── Terms and privacy ─────────────────────────────────────────────────── */

  /** Public: readable without signing in, because a notice you must authenticate to read is not one. */
  policies: () => request<{ policies: T.Policy[]; acceptanceLine: string }>('/api/v1/policies'),
  /* ── Staff contracts ───────────────────────────────────────────────────── */
  /* ── Landlord building agreements ───────────────────────────────────── */

  landlordAgreements: (params: { status?: string } = {}) => {
    const query = new URLSearchParams(
      Object.entries(params).filter(([, value]) => value !== undefined) as [string, string][],
    ).toString();
    return request<{ agreements: T.LandlordAgreementRow[] }>(
      `/api/v1/landlord-agreements${query ? `?${query}` : ''}`,
    );
  },
  landlordAgreement: (id: string) =>
    request<T.LandlordAgreement>(`/api/v1/landlord-agreements/${id}`),
  raiseLandlordAgreement: (payload: {
    propertyName: string;
    landlordName: string;
    landlordEmail: string;
    landlordPhone: string;
    term: string;
    physicalAddress?: string;
  }) =>
    request<T.LandlordAgreement>('/api/v1/landlord-agreements', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  /**
   * Save the company's half — the figures, and optionally the clauses.
   *
   * `document` is left out on a save that never opened the wording, so a
   * routine change to a fee cannot overwrite the text with a stale copy.
   */
  saveLandlordAgreementFields: (
    id: string,
    fields: Record<string, unknown>,
    document?: { body?: string; title?: string },
  ) =>
    request<T.LandlordAgreement>(`/api/v1/landlord-agreements/${id}/fields`, {
      method: 'PUT',
      body: JSON.stringify({ fields, ...(document ?? {}) }),
    }),
  signLandlordAgreementForCompany: (
    id: string,
    payload: { typedName: string; title?: string; fields?: Record<string, unknown> },
  ) =>
    request<T.LandlordAgreement>(`/api/v1/landlord-agreements/${id}/sign/company`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  /**
   * Take back one the landlord has not signed, so its terms can be edited.
   *
   * Removes our signature and revokes their link. The server refuses it once
   * the landlord has signed — that one is amended, not recalled.
   */
  /**
   * Edit an agreement in any state. Admin only.
   *
   * The server records it and, when the agreement is signed, stops claiming
   * the signatures cover the new wording — it does not refuse.
   */
  overrideLandlordAgreement: (
    id: string,
    payload: { fields?: Record<string, unknown>; title?: string; body?: string; reason?: string },
  ) =>
    request<T.LandlordAgreement>(`/api/v1/landlord-agreements/${id}/fields/override`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    }),

  recallLandlordAgreement: (id: string, reason: string) =>
    request<T.LandlordAgreement>(`/api/v1/landlord-agreements/${id}/recall`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),

  /** Raise version N+1 of a signed agreement. Returns the new draft. */
  amendLandlordAgreement: (id: string, reason: string) =>
    request<T.LandlordAgreement>(`/api/v1/landlord-agreements/${id}/amend`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),

  voidLandlordAgreement: (id: string, reason: string) =>
    request<T.LandlordAgreement>(`/api/v1/landlord-agreements/${id}/void`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),

  /*
   * The landlord's own three calls, reached with the token from their email
   * rather than a session. No cookie is involved, which is why they are
   * grouped apart from everything above.
   */
  landlordSigningView: (token: string) =>
    request<T.LandlordSigningView>(`/api/v1/landlord/${encodeURIComponent(token)}`),
  saveLandlordOwnFields: (token: string, fields: Record<string, unknown>) =>
    request<T.LandlordAgreement>(`/api/v1/landlord/${encodeURIComponent(token)}/fields`, {
      method: 'PUT',
      body: JSON.stringify({ fields }),
    }),
  signAsLandlord: (token: string, payload: { typedName: string; fields?: Record<string, unknown> }) =>
    request<T.LandlordAgreement>(`/api/v1/landlord/${encodeURIComponent(token)}`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  myStaffContracts: () =>
    request<{ contracts: T.StaffContractRow[] }>('/api/v1/staff-contracts/mine'),

  /* ── The paying handset ──────────────────────────────── */

  pairedDevices: () =>
    request<{
      devices: Array<{
        id: string;
        label: string;
        platform: string;
        appVersion: string;
        pairedAt: string;
        lastSeenAt: string | null;
        tokenLast4: string;
      }>;
    }>('/api/v1/devices'),

  /** Eight characters, ten minutes, one handset. */
  issuePairingCode: () =>
    request<{ code: string; expiresAt: string }>('/api/v1/devices/pairing-codes', {
      method: 'POST',
    }),

  revokeDevice: (id: string) =>
    request<{ revoked: boolean }>(`/api/v1/devices/${id}`, { method: 'DELETE' }),

  /* ── Personnel paperwork ─────────────────────────────── */

  myDocuments: () => request<T.MyDocuments>('/api/v1/documents/mine'),

  uploadDocument: (payload: { kind: string; data: string; filename: string }) =>
    request<T.EmployeeDocument>('/api/v1/documents/mine', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  /** Everybody's, grouped by person. Needs document:review. */
  documentRoster: () => request<T.DocumentRoster>('/api/v1/documents/roster'),

  reviewDocument: (id: string, payload: { approve: boolean; note?: string }) =>
    request<T.EmployeeDocument>(`/api/v1/documents/${id}/review`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  staffContracts: (params: { status?: string; userId?: string } = {}) => {
    const query = new URLSearchParams(
      Object.entries(params)
        .filter(([, value]) => value !== undefined && value !== '')
        .map(([key, value]) => [key, String(value)]),
    ).toString();
    return request<{ contracts: T.StaffContractRow[] }>(
      `/api/v1/staff-contracts${query ? `?${query}` : ''}`,
    );
  },

  /**
   * Email the employee again that a contract is waiting for them.
   *
   * Needed because every contract raised before that email existed is sitting
   * in "awaiting employee" with nobody told, and nothing retries them on their
   * own.
   */
  remindStaffContract: (id: string) =>
    request<{ sent: boolean; to: string | null }>(
      `/api/v1/staff-contracts/${id}/remind`,
      { method: 'POST' },
    ),

  staffContract: (id: string) => request<T.StaffContract>(`/api/v1/staff-contracts/${id}`),

  raiseStaffContract: (payload: {
    userId: string;
    kind: string;
    startDate: string;
    title?: string;
    body?: string;
    fields?: Record<string, unknown>;
  }) =>
    request<T.StaffContract>('/api/v1/staff-contracts', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  /** Save your own half without signing, so a dropped connection costs nothing. */
  saveStaffContractFields: (id: string, fields: Record<string, unknown>) =>
    request<T.StaffContract>(`/api/v1/staff-contracts/${id}/fields`, {
      method: 'PUT',
      body: JSON.stringify({ fields }),
    }),

  /**
   * Save the company's half of an employment contract, and optionally the
   * letter's own wording. Only this route accepts the wording — the
   * employee's does not, and the server refuses it there regardless.
   */
  saveStaffContractCompanyFields: (
    id: string,
    fields: Record<string, unknown>,
    document?: { body?: string; title?: string },
  ) =>
    request<T.StaffContract>(`/api/v1/staff-contracts/${id}/company-fields`, {
      method: 'PUT',
      body: JSON.stringify({ fields, ...(document ?? {}) }),
    }),

  signStaffContractForCompany: (
    id: string,
    payload: { typedName: string; title?: string; fields?: Record<string, unknown> },
  ) =>
    request<T.StaffContract>(`/api/v1/staff-contracts/${id}/sign/company`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  signStaffContract: (
    id: string,
    payload: { typedName: string; title?: string; fields?: Record<string, unknown> },
  ) =>
    request<T.StaffContract>(`/api/v1/staff-contracts/${id}/sign`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  voidStaffContract: (id: string, reason: string) =>
    request<T.StaffContract>(`/api/v1/staff-contracts/${id}/void`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),

  /* ── Role responsibilities ─────────────────────────────────────────────── */
  outstandingResponsibilities: () =>
    request<{
      outstanding: {
        role: string;
        title: string;
        body: string;
        version: string;
        document: string;
        acceptanceLine: string;
      } | null;
    }>('/api/v1/responsibilities/me/outstanding'),

  acceptResponsibilities: (version: string) =>
    request<{ accepted: boolean; role: string; version: string }>(
      '/api/v1/responsibilities/accept',
      { method: 'POST', body: JSON.stringify({ version }) },
    ),

  allResponsibilities: () =>
    request<{
      responsibilities: Array<{
        role: string;
        title: string;
        body: string;
        version: string;
        updatedById: string | null;
        updatedAt: string;
        holders: number;
        accepted: number;
      }>;
    }>('/api/v1/responsibilities'),

  saveResponsibilities: (
    role: string,
    payload: { title: string; body: string; substantive: boolean },
  ) =>
    request<{ role: string; version: string; changed: boolean; reasked: boolean }>(
      `/api/v1/responsibilities/${role}`,
      { method: 'PUT', body: JSON.stringify(payload) },
    ),

  resetResponsibilities: (role: string) =>
    request<{ role: string; version: string; changed: boolean; reasked: boolean }>(
      `/api/v1/responsibilities/${role}/reset`,
      { method: 'POST' },
    ),

  outstandingPolicies: () =>
    request<{
      outstanding: T.Policy[];
      accepted: { document: string; version: string; acceptedAt: string }[];
      acceptanceLine: string;
    }>('/api/v1/policies/me/outstanding'),
  acceptPolicy: (document: string, version: string) =>
    request<{ accepted: boolean; outstanding: T.Policy[] }>('/api/v1/policies/accept', {
      method: 'POST',
      body: JSON.stringify({ document, version }),
    }),
};
